/* Runtime-generated particle sprites — no asset download cost. Soft
   Gaussian "puff" PNGs are produced once on first request and cached as
   data URLs.

   Each puff uses a 5-stop radial gradient that approximates exp(-2t²) —
   soft Gaussian-ish falloff with a gentle bright core, going fully
   transparent at the edge so overlapping puffs alpha-stack cleanly.

   Why pre-tinted variants instead of @tsparticles/shape-image's
   `replaceColor: true`: the runtime tint flag silently does nothing in
   the current build, so coloured palettes (FIRE_COLORS) all render as
   the white base sprite. Baking one PNG per palette colour and handing
   tsparticles an `image: [...]` array sidesteps the issue — the particle
   randomly picks one tinted sprite at spawn time, and the gradient is
   in the correct colour from frame zero. */

const SIZE = 128;
const SPARKLE_SIZE = 96;

let whitePuff: string | null = null;
const tintedPuffs = new Map<string, string>();
const sparkleSprites = new Map<string, string>();

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace("#", "");
  const v = parseInt(h, 16);
  return { r: (v >> 16) & 0xff, g: (v >> 8) & 0xff, b: v & 0xff };
}

function makePuff(rgb: { r: number; g: number; b: number }): string {
  const c = document.createElement("canvas");
  c.width = SIZE;
  c.height = SIZE;
  const ctx = c.getContext("2d");
  if (!ctx) throw new Error("2d context unavailable");
  const r = SIZE / 2;
  const grad = ctx.createRadialGradient(r, r, 0, r, r, r);
  const { r: rr, g: gg, b: bb } = rgb;
  grad.addColorStop(0.0, `rgba(${rr},${gg},${bb},1.00)`);
  grad.addColorStop(0.25, `rgba(${rr},${gg},${bb},0.62)`);
  grad.addColorStop(0.5, `rgba(${rr},${gg},${bb},0.27)`);
  grad.addColorStop(0.75, `rgba(${rr},${gg},${bb},0.08)`);
  grad.addColorStop(1.0, `rgba(${rr},${gg},${bb},0.00)`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, SIZE, SIZE);
  return c.toDataURL("image/png");
}

export function getPuffSpriteUrl(): string {
  if (!whitePuff) whitePuff = makePuff({ r: 255, g: 255, b: 255 });
  return whitePuff;
}

export function getTintedPuffSpriteUrl(hex: string): string {
  const cached = tintedPuffs.get(hex);
  if (cached) return cached;
  const url = makePuff(hexToRgb(hex));
  tintedPuffs.set(hex, url);
  return url;
}

export const PUFF_SPRITE_SIZE = SIZE;

export type SparkleSpriteKind = "diamond" | "star4" | "star8" | "core";

export interface ParticleImageSprite {
  [key: string]: string | number;
  src: string;
  width: number;
  height: number;
}

function rgba(rgb: { r: number; g: number; b: number }, alpha: number): string {
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
}

function pathStar(
  ctx: CanvasRenderingContext2D,
  points: number,
  outer: number,
  inner: number,
): void {
  const step = Math.PI / points;
  ctx.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const radius = i % 2 === 0 ? outer : inner;
    const angle = -Math.PI / 2 + i * step;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

function makeSparkle(kind: SparkleSpriteKind, rgb: { r: number; g: number; b: number }): string {
  const c = document.createElement("canvas");
  c.width = SPARKLE_SIZE;
  c.height = SPARKLE_SIZE;
  const ctx = c.getContext("2d");
  if (!ctx) throw new Error("2d context unavailable");
  const r = SPARKLE_SIZE / 2;

  const halo = ctx.createRadialGradient(r, r, 0, r, r, r);
  halo.addColorStop(0, rgba(rgb, kind === "core" ? 0.95 : 0.5));
  halo.addColorStop(0.28, rgba(rgb, kind === "core" ? 0.42 : 0.18));
  halo.addColorStop(1, rgba(rgb, 0));
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, SPARKLE_SIZE, SPARKLE_SIZE);

  ctx.save();
  ctx.translate(r, r);
  const fill = ctx.createRadialGradient(0, 0, 0, 0, 0, r * 0.78);
  fill.addColorStop(0, "rgba(255,255,255,1)");
  fill.addColorStop(0.22, "rgba(255,255,255,0.96)");
  fill.addColorStop(0.42, rgba(rgb, 0.88));
  fill.addColorStop(1, rgba(rgb, 0));
  ctx.fillStyle = fill;

  if (kind === "diamond") {
    ctx.beginPath();
    ctx.moveTo(0, -r * 0.82);
    ctx.lineTo(r * 0.28, 0);
    ctx.lineTo(0, r * 0.82);
    ctx.lineTo(-r * 0.28, 0);
    ctx.closePath();
    ctx.fill();
  } else if (kind === "star4") {
    pathStar(ctx, 4, r * 0.86, r * 0.13);
    ctx.fill();
  } else if (kind === "star8") {
    pathStar(ctx, 8, r * 0.82, r * 0.2);
    ctx.fill();
  } else {
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.42, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  return c.toDataURL("image/png");
}

export function getTintedSparkleSpriteUrl(kind: SparkleSpriteKind, hex: string): string {
  const key = `${kind}:${hex}`;
  const cached = sparkleSprites.get(key);
  if (cached) return cached;
  const url = makeSparkle(kind, hexToRgb(hex));
  sparkleSprites.set(key, url);
  return url;
}

export function getSparkleParticleImages(): ParticleImageSprite[] {
  const colors = ["#ffffff", "#ffe69b", "#7dd3fc", "#fb923c"];
  const kinds: SparkleSpriteKind[] = ["diamond", "star4", "star8", "core"];
  return colors.flatMap((hex) =>
    kinds.map((kind) => ({
      src: getTintedSparkleSpriteUrl(kind, hex),
      width: SPARKLE_SIZE,
      height: SPARKLE_SIZE,
    })),
  );
}

export const SPARKLE_SPRITE_SIZE = SPARKLE_SIZE;
